import DataGrid from 'react-data-grid';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { HOME_NEW_RESULT_FLASH_CLASS } from '../../helper/savedSearchNewResults';
import styled from '@emotion/styled';

import {
  GENERATED_COLUMN_HEADER_PROCESSING_CLASS,
  GENERATED_COLUMN_PROCESSING_CLASS,
  GENERATED_COLUMN_RESULT_CLASS,
  GENERATED_ROW_HIGHLIGHT_CLASS,
} from '../../helper/generatedColumn';

export const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  // Height follows the current page of rows (pagination page size), not a
  // fixed viewport — page scroll owns vertical overflow; grid keeps horizontal.
  width: '100%',
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden'
}));

export const GridSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  width: '100%',
}));

export const GridTopBar = styled(Box)(() => ({
  flex: '0 0 auto'
}));

export const GridBody = styled(Box)(() => ({
  flex: '0 0 auto',
  minWidth: 0,
  width: '100%',
  overflow: 'hidden',
  position: 'relative'
}));

export const createEnrichmentGridStyles = (theme) => {
  const rowBg = theme.palette.background.paper;
  // Opaque paints only — translucent rgba shows scrollable cells through sticky columns.
  const headerBg = theme.palette.mode === 'dark' ? '#2A2A2A' : '#F9FAFB';
  const hoverBg = theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5';
  const selectedRowBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.16)
      : 'rgba(25, 118, 210, 0.08)';
  // Selected + paper composite must stay opaque on sticky cells.
  const selectedFrozenBg =
    theme.palette.mode === 'dark' ? '#2B2840' : '#E8F1FB';
  const frozenShadow =
    theme.palette.mode === 'dark'
      ? '2px 0 4px rgba(0, 0, 0, 0.35)'
      : '2px 0 4px rgba(15, 23, 42, 0.06)';

  return {
    '--rdg-color-scheme': theme.palette.mode === 'dark' ? 'dark' : 'light',
    '--rdg-background-color': rowBg,
    '--rdg-color': theme.palette.text.primary,
    '--rdg-border-color': theme.palette.divider,
    '--rdg-header-background-color': headerBg,
    '--rdg-row-hover-background-color': hoverBg,
    '--rdg-cell-frozen-box-shadow': frozenShadow,

    width: '100%',
    border: 'none',
    fontSize: '13px',
    backgroundColor: rowBg,
    color: theme.palette.text.primary,
    // Horizontal scroll only — vertical size matches the page of rows.
    overflowX: 'auto',
    overflowY: 'hidden',
    overscrollBehaviorX: 'contain',

    '& .rdg-header-row': {
      backgroundColor: headerBg,
      color: theme.palette.text.secondary,
      fontWeight: 'bold',
    },
    // The production CSS minifier merges react-data-grid's `@layer`-nested
    // rules, so every header cell also receives the column-resize handle's
    // declarations (`position: absolute; inset-inline-end: 0; inline-size:
    // 10px; cursor: col-resize`). With sticky header cells, `inset-inline-end:
    // 0` pins any column that overflows the viewport (e.g. generated
    // enrichment columns) to the right edge, on top of the base columns.
    // These unlayered overrides win over `@layer` rules and restore the
    // intended values.
    '& .rdg-header-row > .rdg-cell': {
      cursor: 'pointer',
      inlineSize: '100%',
      insetInlineEnd: 'auto',
      minWidth: 0,
      backgroundColor: headerBg,
      // Keep room for the active-sort chevron next to the title.
      overflow: 'hidden',
    },
    '& .rdg-header-row > .rdg-cell .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
      opacity: 0.72,
    },
    '& .rdg-header-row > .rdg-cell-frozen': {
      position: 'sticky',
      backgroundColor: headerBg,
      zIndex: 4,
    },
    '& .rdg-row': {
      backgroundColor: rowBg,
      color: theme.palette.text.primary,
      // Whole row is tappable (opens company) — not just the Name cell.
      cursor: 'pointer',
    },
    // V2: flat white rows (pre-mobile desktop look). V1 keeps an opaque stripe.
    '& .rdg-row:nth-of-type(even)': {
      backgroundColor: rowBg,
    },
    '& .rdg-row:hover': {
      backgroundColor: hoverBg,
    },
    '& .rdg-row:nth-of-type(even):hover': {
      backgroundColor: hoverBg,
    },
    '& .rdg-cell': {
      // V2 drops vertical rules for a lighter spreadsheet look; keep row dividers.
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      // Inherit opaque row paints so hover / selected / stripe stay consistent.
      backgroundColor: 'inherit',
      overflow: 'hidden',
      insetInlineEnd: 'auto',
    },
    // Keep non-frozen cells relative for absolute children; never override sticky frozen.
    '& .rdg-cell:not(.rdg-cell-frozen)': {
      position: 'relative',
    },
    '& .rdg-cell-frozen': {
      position: 'sticky',
      // Explicit opaque paint — never rely on translucent overlays.
      backgroundColor: rowBg,
      // Above scrolling body cells; below sticky header frozen cells.
      zIndex: 2,
    },
    '& .rdg-row:nth-of-type(even) > .rdg-cell-frozen': {
      backgroundColor: rowBg,
    },
    // Paint every cell on row hover so sticky Name matches the rest of the row.
    // Include :nth-of-type(even) so striped V1 rows don't win on specificity.
    '& .rdg-row:hover > .rdg-cell, & .rdg-row:nth-of-type(even):hover > .rdg-cell': {
      backgroundColor: hoverBg,
    },
    '& .rdg-row:hover > .rdg-cell-frozen, & .rdg-row:nth-of-type(even):hover > .rdg-cell-frozen': {
      backgroundColor: hoverBg,
    },
    '& .rdg-checkbox-container': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .rdg-row-selected': {
      backgroundColor: selectedRowBg,
    },
    '& .rdg-row-selected > .rdg-cell-frozen': {
      backgroundColor: selectedFrozenBg,
    },
    '& .rdg-row-selected:hover': {
      backgroundColor: selectedRowBg,
    },
    '& .rdg-row-selected:hover > .rdg-cell': {
      backgroundColor: selectedRowBg,
    },
    '& .rdg-row-selected:hover > .rdg-cell-frozen': {
      backgroundColor: selectedFrozenBg,
    },
    // Prefer row-level affordance over a single focused cell ring.
    '& .rdg-cell[aria-selected="true"]': {
      outline: 'none',
    },
    // Generated-column loading highlight (primary borders + tinted header/cells).
    [`& .rdg-cell.${GENERATED_COLUMN_PROCESSING_CLASS}`]: {
      borderLeft: `2px solid ${theme.palette.primary.main}`,
      borderRight: `2px solid ${theme.palette.primary.main}`,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.14)
          : 'rgba(74, 44, 245, 0.04)',
    },
    [`& .rdg-header-row > .rdg-cell.${GENERATED_COLUMN_HEADER_PROCESSING_CLASS}`]: {
      borderLeft: `2px solid ${theme.palette.primary.main}`,
      borderRight: `2px solid ${theme.palette.primary.main}`,
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.26)
          : 'rgba(74, 44, 245, 0.10)',
    },
    [`& .rdg-row:nth-of-type(even) > .rdg-cell.${GENERATED_COLUMN_PROCESSING_CLASS}`]: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.16)
          : 'rgba(74, 44, 245, 0.055)',
    },
    // Settled AI result: light-purple cell fill (design-system secondary / primary wash).
    [`& .rdg-cell.${GENERATED_COLUMN_RESULT_CLASS}`]: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.18)
          : theme.palette.secondary.main,
    },
    [`& .rdg-row:nth-of-type(even) > .rdg-cell.${GENERATED_COLUMN_RESULT_CLASS}`]: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.2)
          : theme.palette.secondary.main,
    },
    [`& .rdg-row.rdg-row-selected > .rdg-cell.${GENERATED_COLUMN_RESULT_CLASS}`]: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.22)
          : theme.palette.secondary.main,
    },
    // Row containing AI-generated content: subtle primary-purple stroke.
    [`& .rdg-row.${GENERATED_ROW_HIGHLIGHT_CLASS}`]: {
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    },
    // Home "+N new" entry: temporary very light purple row flash (designer, 469041a3).
    [`& .rdg-row > .rdg-cell`]: {
      transition: 'background-color 0.55s ease',
    },
    [`& .rdg-row.${HOME_NEW_RESULT_FLASH_CLASS} > .rdg-cell`]: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.14)
          : alpha(theme.palette.primary.main, 0.06),
    },

    // —— Mobile-only overrides (do not affect desktop V2) ——
    '&.enrichment-grid-mobile .rdg-row': {
      cursor: 'pointer',
    },
    '&.enrichment-grid-mobile .rdg-cell[aria-selected="true"]': {
      outline: 'none',
    },
    '&.enrichment-grid-mobile .rdg-cell-drag-handle': {
      display: 'none',
    },
    '&.enrichment-grid-mobile .rdg-focus-sink::before': {
      content: 'none',
      display: 'none',
    },
  };
};

export const StyledDataGrid = styled(DataGrid)(({ theme }) =>
  createEnrichmentGridStyles(theme)
);
